import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import { Form } from "antd";
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import { FieldTypes } from 'utils/formUtils';
import Vocabulary from 'components/Vocabulary';
import ProductAPI from 'restAPI/ProductAPI';
import SubProductAPI from 'restAPI/SubProductAPI';
import BaseModalForm from 'components/BaseModalForm';
import { Environments } from 'views/Findings/Constants';
import { MODAL_SIZE_MEDIUM } from 'constants/Layout';
import message from 'components/CustomMessage';

const ALL_PRODUCTS_CONFIG = {
    id: 'all',
    name: 'All Products'
};

const ALL_SUBPRODUCTS_CONFIG = {
    id: 'all',
    name: 'All Subproducts'
};

export default function MSTeamsMappingForm({ config, isEdit, onUpdateConfigComponent, loginId, afterClose }) {
    const [form] = Form.useForm();
    const thisRef = useRef();
    const [formData, setFormData] = useState({});
    const [selectedProduct, setSelectedProduct] = useState(config?.apId);
    const [selectedTeam, setSelectedTeam] = useState(config?.teamId)
    const [products, setProducts] = useState();
    const [subProducts, setSubProducts] = useState([]);
    const [teams, setTeams] = useState([]);
    const [channels, setChannels] = useState([]);
    
    const setInitialValue = useCallback((form, data) => {
        const { aeName, apId, aspId, teamId, channelIdsAndNames, notificationLevels } = data;
        const channelId = Object.keys(channelIdsAndNames)
        const initialProductId = +apId || 'all';
        const initialSubProductId = +aspId || 'all';
        form.setFieldsValue({ aeId: aeName, apId: initialProductId, teamId, channelId, alerts: notificationLevels?.alerts })
        if (initialProductId !== 'all') {
            const { promise: subProductPromise } = SubProductAPI.getAllQuick(apId);
            subProductPromise.then((responseProductsList) => {
                const allProductsList = [
                    {
                        ...ALL_SUBPRODUCTS_CONFIG
                    },
                    ...responseProductsList,
                ];
                setSubProducts(allProductsList);
            }, _.noop);
        } else {
            setSubProducts([{
                ...ALL_SUBPRODUCTS_CONFIG
            }]);
        }
        form.setFieldsValue({ aspId: initialSubProductId });

        const { promise: teamPromise } = ToolsIntegrationsAPI.getMSTeamsChannel(loginId, teamId);
        teamPromise.then(setChannels, _.noop);
        form.setFieldsValue({ channelId });
    }, [loginId])

    useEffect(() => {
        if (isEdit && form) {
            setInitialValue(form, config)
        }
    }, [isEdit, form, config, setInitialValue])

    const getProducts = useCallback(() => {
        const { promise, cancel } = ProductAPI.getAllQuick();
        promise.then((responseProductsList) => {
            const allProductsList = [
                {
                    ...ALL_PRODUCTS_CONFIG
                },
                ...responseProductsList,
            ];
            setProducts(allProductsList);
        }, _.noop);
        return cancel;
    }, [])

    const getTeams = useCallback(() => {
        const { promise, cancel } = ToolsIntegrationsAPI.getMSTeams(loginId);
        promise.then(setTeams, _.noop)
        return cancel;
    }, [loginId])

    useEffect(() => {
        getProducts();
        getTeams();
    }, [getProducts, getTeams]);

    useEffect(() => {
        if (formData.apId && form && selectedProduct !== 'all') {
            form.setFieldsValue({ aspId: undefined, aeId: undefined });
            setSubProducts(null);
            const { promise, cancel } = SubProductAPI.getAllQuick(formData.apId);
            promise.then((responseSubProductsList) => {
                const allProductsList = [
                    {
                        ...ALL_SUBPRODUCTS_CONFIG
                    },
                    ...responseSubProductsList,
                ];
                setSubProducts(allProductsList);
            }, _.noop);
            return cancel;
        } else if (formData.apId && form && selectedProduct === 'all') {
            setSubProducts([{
                ...ALL_SUBPRODUCTS_CONFIG
            }]);
        }
    }, [formData.apId, form, selectedProduct]);

    useEffect(() => {
        if (formData.teamId && form && selectedTeam) {
            const { promise, cancel } = ToolsIntegrationsAPI.getMSTeamsChannel(loginId, formData.teamId);
            promise.then(setChannels, _.noop);
            return cancel;
        }
    }, [formData.teamId, form, selectedTeam, loginId]);

    const saveConfig = useCallback(async (values) => {
        const { apId, aspId, aeId, teamId, channelId } = values
        const channelIdsAndNames = channelId.reduce((acc, channel) => {
            acc[channel] = _.find(channels, { id: channel })?.name;
            return acc;
          }, {});
          
        const data = {
            configId: loginId,
            teamId: teamId,
            teamName: _.find(teams, { id: teamId })?.name,
            channelIdsAndNames: channelIdsAndNames,
            apId: apId === ALL_PRODUCTS_CONFIG.id ? null : apId,
            apName: _.find(products, { id: apId })?.name,
            aspId: aspId === ALL_SUBPRODUCTS_CONFIG.id ? null : aspId,
            aspName: _.find(subProducts, { id: aspId })?.name,
            aeName: aeId,
            enabled: true,
            notificationLevels: {
                alerts: values.alerts
            },
        }
        const newData = _.omitBy(data, (item) => item === undefined || item === null)
        if (isEdit) {
            await ToolsIntegrationsAPI.updateMSTeamIntegrationDetails(newData, config?.id).promise;
            message.success("Successfully Updated MS Teams Mapping")
        }
        else {
            await ToolsIntegrationsAPI.createMSTeamsIntegration(newData).promise;
            message.success("Successfully Added MS Teams Mapping");
        }
        onUpdateConfigComponent && onUpdateConfigComponent();

    }, [config, isEdit, onUpdateConfigComponent, products, subProducts, teams, channels, loginId]);

    

    const fields = useMemo(() => [
        {
            name: "apId",
            label: <Vocabulary name="product" />,
            required: true,
            type: FieldTypes.SELECT,
            options: products,
            loading: _.isEmpty(products),
            colSpan: 12,
            props: {
                autoFocus: !isEdit,
                onChange: value => {
                    setSubProducts(null);
                    setSelectedProduct(value)
                }
            }
        }, {
            name: "aspId",
            label: <Vocabulary name="subProduct" />,
            required: true,
            type: FieldTypes.SELECT,
            options: subProducts,
            loading: _.isNull(subProducts),
            colSpan: 12
        }, {
            name: "aeId",
            label: "Environment",
            required: true,
            type: FieldTypes.SELECT,
            options: Environments,
            colSpan: 12,
        }, {
            name: 'teamId',
            label: 'Team',
            required: true,
            type: FieldTypes.SELECT,
            colSpan: 12,
            options: teams,
            props: {
                onChange: value => {
                    setChannels(null)
                    form.setFieldsValue({
                        channelId: undefined
                    })
                    setSelectedTeam(value)
                }
            }
        },
        {
            name: 'channelId',
            label: 'Channel Name',
            required: true,
            type: FieldTypes.MULTI_SELECT,
            colSpan: 12,
            options: channels,
            loading: _.isNull(channels),
            rules: [{
                validator: (rule, value)=>{
                    if(value && value.length > 20){
                        return Promise.reject("Maximum 20 channels can be selected")
                    }
                    return Promise.resolve()
                }
            }]
        },
        {
            name: "alerts",
            label: "Notification Levels",
            type: FieldTypes.CHECKBOX_GROUP,
            labelAlign: "left",
            options: ["Critical", "High", "Medium", "Low"],
        }
    ], [channels, isEdit, products, subProducts, teams, form])

    const modalProps = {
        width: MODAL_SIZE_MEDIUM
    }
    return <BaseModalForm
        ref={thisRef}
        modalProps={modalProps}
        afterClose={afterClose}
        title={isEdit ? "Edit MS Teams Mapping" : "Add Mapping"}
        onValuesChange={(data) => setFormData({ ...formData, ...data })}
        form={form}
        onOk={saveConfig}
        fields={fields}
    />
}
